import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Layout, Spin, Alert, Progress, Card, Row, Col, Typography, Button, Tag } from 'antd';
import { ArrowLeftOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom'; 
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from 'recharts';
import Sidebar from '../components/Sidebar';
import AppHeader from '../components/Header';
import { decryptData } from '../middleware/encryption';

const { Content } = Layout;
const { Title, Text } = Typography;

const COLORS = ['#4CAF50', '#F44336', '#FF9800', '#9E9E9E'];

const VerifyResult = () => {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);
  const navigate = useNavigate();

  const fetchResult = async () => {
    const token = sessionStorage.getItem('access_token');
    if (!token) {
      setError('No access token found');
      setLoading(false);
      return;
    }

    try {
      const res = await fetch(`https://api.app.exellius.com/api/bounce/result/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (!res.ok) throw new Error('Fetch failed');

      const result = await res.json();
      const decrypted = decryptData(result.data);
      setData(decrypted.data);
      setLastUpdated(new Date());
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchResult();
    const interval = setInterval(fetchResult, 5000);
    return () => clearInterval(interval);
  }, [id]);

  const handleDownload = async () => {
    const token = sessionStorage.getItem('access_token');

    try {
      const res = await fetch(`https://api.app.exellius.com/api/bounce/download/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${data.file_name || 'result'}.csv`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error(err);
    }
  };

  const chartData = data
    ? [
        { name: 'Deliverable', value: data.valid_count },
        { name: 'Undeliverable', value: data.invalid_count },
        { name: 'Risky', value: data.risky_count },
        { name: 'Unknown', value: data.unknown_count }
      ]
    : [];

  const total = data?.total_emails || 0;

  const getPercent = (value) => (total ? ((value / total) * 100).toFixed(1) : 0);

  const progressStatus = data?.status === 'completed' ? 'success' : 'active';

  if (loading) {
    return (
      <Layout style={{ minHeight: '100vh' }}>
        <Sidebar />
        <Layout>
          <AppHeader />
          <Content style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
            <Spin size="large" />
          </Content>
        </Layout>
      </Layout>
    );
  }

  if (error) {
    return (
      <Layout style={{ minHeight: '100vh' }}>
        <Sidebar />
        <Layout>
          <AppHeader />
          <Content style={{ padding: 24 }}>
            <Alert message="Error" description={error} type="error" showIcon />
          </Content>
        </Layout>
      </Layout>
    );
  }

  return (
    <Layout style={{ minHeight: '100vh', background: '#f5f7fb' }}>
      <Sidebar />
      <Layout>
        <AppHeader />
        <Content style={{ margin: 24 }}>

          

          {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
  <div>
    <Button 
      onClick={() => navigate(-1)} 
      icon={<ArrowLeftOutlined />}
    >
      Back
    </Button>
  </div>
  
  <div style={{ flex: 1, marginLeft: 20 }}>
    <Title level={4} style={{ margin: 0 }}>{data.file_name}</Title>
    <Text type="secondary">Created {new Date(data.created_at).toLocaleString()}</Text>
  </div>
  
  <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
    <Tag color="processing">Live refresh every 5 seconds</Tag>
    {lastUpdated && (
      <Text type="secondary">Updated {lastUpdated.toLocaleTimeString()}</Text>
    )}
    <Button type="primary" danger onClick={handleDownload} disabled={data.status !== 'completed'}>
      Download results
    </Button>
  </div>
</div>

          {/* Summary Card */}
          <Card title="List summary" style={{ borderRadius: 10 }}>
            <Row gutter={20} align="middle">

              {/* Chart */}
              <Col span={8} style={{ position: 'relative', minHeight: 260 }}>
                <ResponsiveContainer width="100%" height={260}>
                  <PieChart>
                    <Pie
                      data={chartData}
                      dataKey="value"
                      nameKey="name"
                      cx="50%"
                      cy="50%"
                      innerRadius={72}
                      outerRadius={100}
                      paddingAngle={4}
                      isAnimationActive
                      animationDuration={800}
                    >
                      {chartData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip 
                      contentStyle={{ backgroundColor: '#ffffff', border: '2px solid #ccc', borderRadius: 6 }}
                      formatter={(value, name, props) => [`${value}`, props.payload.name]}
                    />
                  </PieChart>
                </ResponsiveContainer>
                <div style={{ position: 'absolute', inset: 0, display: 'flex', justifyContent: 'center', alignItems: 'center', pointerEvents: 'none' }}>
                  <div style={{ textAlign: 'center' }}>
                    <Text strong style={{ fontSize: 24 }}>
                      {data.processed_emails}
                    </Text>
                    <div style={{ color: '#888' }}>Processed</div>
                  </div>
                </div>
              </Col>

              {/* Stats */}
              <Col span={16}>

                <Row style={{ marginBottom: 15 }} justify="space-between">
                  <Col>
                    <Text strong>Deliverable</Text>
                    <div style={{ color: '#888' }}>Valid emails</div>
                  </Col>
                  <Col>
                    {data.valid_count} ({getPercent(data.valid_count)}%)
                  </Col>
                </Row>

                <Row style={{ marginBottom: 15 }} justify="space-between">
                  <Col>
                    <Text strong>Undeliverable</Text>
                    <div style={{ color: '#888' }}>Invalid emails</div>
                  </Col>
                  <Col>
                    {data.invalid_count} ({getPercent(data.invalid_count)}%)
                  </Col>
                </Row>

                <Row style={{ marginBottom: 15 }} justify="space-between">
                  <Col>
                    <Text strong>Risky</Text>
                    <div style={{ color: '#888' }}>Uncertain deliverability</div>
                  </Col>
                  <Col>
                    {data.risky_count} ({getPercent(data.risky_count)}%)
                  </Col>
                </Row>

                <Row justify="space-between">
                  <Col>
                    <Text strong>Unknown</Text>
                    <div style={{ color: '#888' }}>Server not reachable</div>
                  </Col>
                  <Col>
                    {data.unknown_count} ({getPercent(data.unknown_count)}%)
                  </Col>
                </Row>

              </Col>
            </Row>
          </Card>

          {/* Progress */}
          <Card style={{ marginTop: 20 }}>
            <Text strong>Processing Progress</Text>
            <div style={{ marginTop: 12, transition: 'all 0.5s ease' }}>
              <Progress
                percent={data.progress}
                status={progressStatus}
                strokeLinecap="round"
                showInfo
                strokeWidth={14}
              />
            </div>
          </Card>

        </Content>
      </Layout>
    </Layout>
  );
};

export default VerifyResult;